import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { FaHeart, FaShareAlt } from "react-icons/fa";
import ShareModal from "../../components/modals/ShareModal";
import QueryBoundary from "../../components/ui/QueryBoundary";
import useApiQuery from "../../hooks/useApiQuery";
import useScrollTop from "../../hooks/useScrollTop";
import endpoints from "../../api/endpoints";
import { detailPath, flattenCredits } from "../../lib/catalog";
import userImage from "../../images/default-user-image.png";
import ActorContact from "./ActorContact";

const Fact = ({ label, value }) =>
  value ? (
    <p>
      {label}: {value}
    </p>
  ) : null;

const ActorPage = () => {
  const { id } = useParams();
  const query = useApiQuery(endpoints.actor(id));
  const [sharing, setSharing] = useState(false);
  useScrollTop([id]);

  return (
    <QueryBoundary {...query}>
      {(actor) => {
        const credits = flattenCredits(actor.episode_actors);
        const birthdate =
          actor.year_birthdate && actor.month_birthdate && actor.day_birthdate
            ? `${actor.year_birthdate}/${actor.month_birthdate}/${actor.day_birthdate}`
            : null;

        return (
          <div className="mt-20 flex flex-col gap-12 p-8 md:mb-[25rem] md:mt-10 md:p-[5rem]">
            <div className="gap-[5rem] md:flex md:flex-row">
              <div className="flex flex-col items-center sm:flex-row">
                <img
                  className="h-48 w-48 rounded-full object-cover"
                  src={actor.image || userImage}
                  alt={actor.name}
                />
              </div>

              <div className="relative mt-4 gap-1 md:flex md:flex-col">
                <h2 className="mb-4 flex items-center text-2xl font-bold">
                  من کیستم؟
                  <button
                    type="button"
                    onClick={() => setSharing(true)}
                    aria-label="اشتراک‌گذاری"
                  >
                    <FaShareAlt className="m-4 cursor-pointer text-2xl text-gray-300 transition hover:text-white md:text-3xl" />
                  </button>
                </h2>

                <Fact label="نام" value={actor.name} />
                <Fact label="تاریخ تولد" value={birthdate} />
                <Fact label="شهرستان" value={actor.city} />
                <Fact label="رشته" value={actor.school_major} />
                <Fact label="مقطع" value={actor.grade_degree} />
                <Fact
                  label="پایه"
                  value={
                    actor.grade
                      ? actor.grade < 12
                        ? actor.grade
                        : "بزرگسال"
                      : null
                  }
                />
              </div>
            </div>

            {actor.description && (
              <div>
                <h2 className="mb-4 text-2xl font-bold">درباره من</h2>
                <p style={{ whiteSpace: "pre-line" }}>{actor.description}</p>
              </div>
            )}

            {actor.honors && (
              <div>
                <h2 className="mb-4 text-2xl font-bold">افتخارات</h2>
                <p style={{ whiteSpace: "pre-line" }}>{actor.honors}</p>
              </div>
            )}

            <div>
              <h2 className="mb-3 text-2xl font-bold leading-9">آثار</h2>
              {credits.length > 0 ? (
                <ul className="grid grid-cols-2 gap-2 md:grid-cols-3 md:gap-4 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6">
                  {credits.map((item) => (
                    <li
                      key={`${item.type}-${item.id}`}
                      className="group relative"
                    >
                      <Link to={detailPath(item)}>
                        <div className="relative">
                          <img
                            src={item.cover}
                            alt={item.name}
                            className="h-[19rem] w-[13rem] rounded object-cover"
                            loading="lazy"
                          />
                          <div className="absolute inset-0 hidden rounded bg-black/30 group-hover:block" />
                          <div className="absolute bottom-4 right-4 hidden group-hover:block">
                            {item.like_percent != null && (
                              <p>
                                <FaHeart className="mb-1 ml-1 inline-block text-xl text-gray-300" />
                                {item.like_percent}%
                              </p>
                            )}
                            <p className="mt-1 text-xs">
                              کارگردان : {item.director?.[0]?.name ?? "نامشخص"}
                            </p>
                          </div>
                        </div>
                        <span className="mr-3 mt-2 block">{item.name}</span>
                      </Link>
                    </li>
                  ))}
                </ul>
              ) : (
                <p>-</p>
              )}
            </div>

            <ActorContact
              communication={actor.communication}
              ways={actor.communication_ways}
            />

            <ShareModal
              open={sharing}
              onClose={() => setSharing(false)}
              id={actor.id}
              type="portfolio"
            />
          </div>
        );
      }}
    </QueryBoundary>
  );
};

export default ActorPage;
